<template>
  <v-row no-gutters>
    <customer-list v-if="userData.role === 'PLSYR' && $vuetify.breakpoint.xsOnly" />
    <v-col v-if="appStore.isCustomer" class="delayEl">
      <CustomerAlert />
    </v-col>
    <v-col v-else>
      <div id="payment-tracking-list" :class="{ 'page-fullscreen': pageStore.isFullScreen }">
        <v-card
          tile
          :disabled="store.cardDisabled"
          :class="$vuetify.breakpoint.mdAndDown ? null : 'rounded-t-xl'"
        >
          <PageHeader />

          <v-data-table
            :headers="store.tableColumns"
            :items="store.tableItems"
            :loading="store.tableLoading"
            :height="pageStore.tableHeight"
            loader-height="2"
            :items-per-page="-1"
            :mobile-breakpoint="100"
            fixed-header
            hide-default-footer
          >
            <!-- Arama Alanı -->
            <template v-slot:body.prepend>
              <tr class="data-table-body-search">
                <!-- Belge Tipi -->
                <td class="pa-0">
                  <v-text-field
                    v-model="store.tableSearch_1"
                    :prepend-inner-icon="icons.mdiMagnify"
                    persistent-placeholder
                    clearable
                    placeholder="Ara"
                    filled
                    class="rounded-0 elevation-0"
                    hide-details
                  />
                </td>

                <!-- Belge No -->
                <td class="pa-0">
                  <div class="d-flex">
                    <v-text-field
                      v-model="store.tableSearch_2"
                      :prepend-inner-icon="icons.mdiMagnify"
                      persistent-placeholder
                      clearable
                      placeholder="Ara"
                      filled
                      class="rounded-0 elevation-0"
                      hide-details
                    />
                  </div>
                </td>

                <!-- Cari Unvan -->
                <td class="pa-0">
                  <div class="d-flex">
                    <v-text-field
                      v-model="store.tableSearch_3"
                      :prepend-inner-icon="icons.mdiMagnify"
                      persistent-placeholder
                      clearable
                      placeholder="Ara"
                      filled
                      class="rounded-0 elevation-0"
                      hide-details
                    />
                  </div>
                </td>

                <!-- Boş -->
                <td
                  class="pa-0"
                  v-for="item in store.tableEmptySearch"
                  :key="`emptySearch-${item}`"
                >
                  <v-text-field filled disabled class="rounded-0 elevation-0" hide-details />
                </td>
              </tr>
            </template>

            <!-- Belge Tipi -->
            <template #[`item.FINDOCTYPE`]="{ item }">
              <v-chip label color="chip" class="d-flex justify-center">
                <div
                  class="text-truncate text-uppercase data-table-item-type-one"
                  style="width: 40px !important"
                >
                  {{ item.FINDOCTYPE }}
                </div>
              </v-chip>
            </template>

            <!-- Belge No -->
            <template #[`item.FINDOCNUM`]="{ item }">
              <v-chip color="grey" class="d-flex justify-center" label>
                <div class="text-truncate data-table-item-type-one" style="width: 90px !important">
                  {{ item.FINDOCNUM }}
                </div>
              </v-chip>
            </template>

            <!-- Cari Unvan -->
            <template #[`item.ACCNAME`]="{ item }">
              <div class="d-flex align-center">
                <div>
                  <div class="text-subtitle-2 white--text text-capitalize text-no-wrap">
                    {{ item.ACCNAME }}
                  </div>
                </div>
              </div>
            </template>

            <!-- Vade Tarih -->
            <template #[`item.DUEDATE`]="{ item }">
              <v-tooltip color="chip" bottom v-if="item.DUEDATE">
                <template v-slot:activator="{ on, attrs }">
                  <v-chip color="" class="d-flex justify-center" label v-bind="attrs" v-on="on">
                    <div
                      class="text-truncate d-flex align-center data-table-item-type-one"
                      style="width: 100px !important"
                    >
                      <v-icon small class="mr-2">
                        {{ icons.mdiCalendar }}
                      </v-icon>
                      <div class="mr-1">
                        {{ item.DUEDATE }}
                      </div>
                    </div>
                  </v-chip>
                </template>
                <div>
                  {{ item.DUEDATE }}
                </div>
              </v-tooltip>
            </template>

            <!-- Ödeme Tarih -->
            <template #[`item.POSTDATE`]="{ item }">
              <v-tooltip color="chip" bottom v-if="item.POSTDATE">
                <template v-slot:activator="{ on, attrs }">
                  <v-chip color="" class="d-flex justify-center" label v-bind="attrs" v-on="on">
                    <div
                      class="text-truncate d-flex align-center data-table-item-type-one"
                      style="width: 100px !important"
                    >
                      <v-icon small class="mr-2">
                        {{ icons.mdiCalendar }}
                      </v-icon>
                      <div class="mr-1">
                        {{ item.POSTDATE }}
                      </div>
                    </div>
                  </v-chip>
                </template>
                <div>
                  {{ item.POSTDATE }}
                </div>
              </v-tooltip>
            </template>

            <!-- Tutar -->
            <template #[`item.DCREDIT`]="{ item }">
              <div v-if="item.DCREDIT" class="text-caption white--text font-weight-medium">
                {{ item.DCREDIT | currency({ symbol: "" }) }}
              </div>
            </template>
          </v-data-table>
        </v-card>
      </div>
    </v-col>
  </v-row>
</template>

<script>
import { onMounted, onUnmounted, watch } from "vue-demi";

// Pinia
import { usePaymentTrackingStore } from "./_store";
import { useAppStore } from "@/stores/app";
import { usePageStore } from "@/stores/pageHeader";

// Components
import PageHeader from "@/components/PageHeader/Index.vue";
import CustomerAlert from "@/components/CustomerAlert.vue";
import CustomerList from "@/components/CustomerList.vue";
// Icons
import { mdiRadar, mdiMagnify, mdiCalendar } from "@mdi/js";

export default {
  components: {
    PageHeader,
    CustomerAlert,
    CustomerList
  },
  setup() {
    const userData = JSON.parse(localStorage.getItem("userData"));
    // Pinia bağlantıları
    const store = usePaymentTrackingStore();
    const appStore = useAppStore();
    const pageStore = usePageStore();

    // Sayfa Özellikleri
    const pageSettings = () => {
      pageStore.pageTitle = "Ödemeler";
      pageStore.pageIcon = mdiRadar;
      pageStore.hasFullScreen = true;
      pageStore.hasDownload = true;
      pageStore.hasDateRangeAll = false;
      pageStore.hasDateRange = true;
      pageStore.hasActions = false;

      pageStore.downloadHeader = {
        "Belge No": "FINDOCNUM",
        "Belge Tipi": "FINDOCTYPE",
        "Cari Unvan": "ACCNAME",
        "Vade Tarihi": "DUEDATE",
        "Ödeme Tarihi": "POSTDATE",
        "Tutar (TL)": "DCREDIT",
      };
    };

    // Müşteri seçimi gerekiyorsa değişiklikler izleniyor
    watch(
      () => appStore.selectedCustomer,
      (newVal, oldVal) => {
        if (!appStore.isCustomer) {
          store.tableFetch(pageStore.dateRange);
        }
      }
    );

    // Tarih aralığı için değişiklikler izleniyor
    watch(
      () => pageStore.dateRange,
      (newVal, oldVal) => {
        if (!appStore.isCustomer) {
          store.tableFetch(newVal);
        }
      }
    );

    onMounted(() => {
      pageSettings();
      if (appStore.isCustomer) return;

      store.tableFetch(pageStore.dateRange);
    });

    onUnmounted(() => {
      pageStore.pageSettingsClear();
    });

    return {
      // Pinia
      store,
      appStore,
      pageStore,
      userData,
      // icons
      icons: {
        mdiRadar,
        mdiMagnify,
        mdiCalendar,
      },
    };
  },
};
</script>
